import { useHistory } from 'react-router';
import { DateTime } from 'luxon';
import { Session } from '@mindly/ui-components';
import { useCallback, useState } from 'react';
import { isPlatform } from '@ionic/react';
import { Jitsi } from 'capacitor-jitsi-meet';
import { PrivateRoutes } from '../../routes/navigation';
import { useIsSafariIsNotSupported } from '../../hooks/useIsSafariIsNotSupported';
import { useSelector } from 'react-redux';
import { environment } from 'src/enviroments';
import { getJitsiVideoCallToken, isBetaTester } from 'src/api/videoCall';
import { notifyAboutJoinToTheVideoChat } from '../../api/notifications';
import { isVideoCallActiveSelector } from 'src/redux/videoCall/selector';
import { useAnalytics, useLog } from '..';

export const useVideoCall = () => {
  const isVideoCallActive = useSelector(isVideoCallActiveSelector);
  const [isLoading, setIsLoading] = useState(false);
  const { setIsOpenNotSupportedModal, isSafariIsNotSupported } = useIsSafariIsNotSupported();
  const history = useHistory();
  const { trackPredefinedEvent } = useAnalytics();

  const { logError } = useLog();

  const handleModalVideoClick = useCallback(
    async (session: Session) => {
      if (isVideoCallActive) return;

      if (!session) return;
      if (isSafariIsNotSupported) return setIsOpenNotSupportedModal(true);

      setIsLoading(true);

      let isNew = false;

      try {
        const testersData = await isBetaTester(session.psychologist_id);
        if (testersData.data) {
          isNew = true;
        }
      } catch (error) {
        isNew = false;
      }

      const trackVideoCallStarted = (_s: Session, isNewVideo: boolean) =>
        trackPredefinedEvent('SESSION_STARTED', {
          specialist_id: _s?.psychologist_id,
          specialist_name: _s?.psychologist_name,
          consultation_id: _s?.id,
          is_trial: !!_s?.payment_intent_id,
          is_new_video: isNewVideo,
          is_hybrid_platform: isPlatform('hybrid'),
          scheduled_start_time:
            typeof _s?.consultation_time === 'string'
              ? _s?.consultation_time
              : DateTime.fromSeconds(_s?.consultation_time?._seconds ?? 0).toISO(),
          actual_start_time: DateTime.utc().toISO(),
        });

      if (isPlatform('hybrid') && isNew) {
        try {
          const token = await getJitsiVideoCallToken(session.id);
          await Jitsi.joinConference({
            roomName: `${environment.JITSI_APP_ID}/${session.id}`,
            url: 'https://8x8.vc',
            token: token.data,
            featureFlags: {
              'add-people.enabled': false,
              'breakout-rooms.enabled': false,
              'calendar.enabled': false,
              'call-integration.enabled': false,
              'close-captions.enabled': false,
              'chat.enabled': true,
              'help.enabled': false,
              'invite.enabled': false,
              'invite-dial-in.enabled': false,
              'ios.screensharing.enabled': false,
              'android.screensharing.enabled': false,
              'live-streaming.enabled': false,
              'lobby-mode.enabled': true,
              'meeting-name.enabled': false,
              'meeting-password.enabled': false,
              'prejoinpage.enabled': true,
              'prejoinpage.hideDisplayName': true,
              'recording.enabled': false,
              'security-options.enabled': false,
              'settings.enabled': false,
            },
            subject: ' ',
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            chatEnabled: true,
            inviteEnabled: false,
            configOverrides: {
              readOnlyName: true,
              disablePolls: true,
              disableProfile: true,
              hideEmailInSettings: true,
              disableInviteFunctions: true,
              hideDisplayName: true,
              prejoinConfig: {
                enabled: true,
                hideDisplayName: true,
              },
              recording: {
                recordAudioAndVideo: false,
                showPrejoinWarning: false,
              },
              giphy: {
                enabled: false,
              },
              participantsPane: {
                enabled: true,
                hideModeratorSettingsTab: true,
                hideMoreActionsButton: true,
                hideMuteAllButton: true,
              },
              videoQuality: {
                mobileCodecPreferenceOrder: ['H264', 'VP8', 'VP9'],
                codecPreferenceOrder: ['H264', 'VP8', 'VP9'],
              },
              resolution: 1080,
              p2p: {
                enabled: true,
                mobileCodecPreferenceOrder: ['H264', 'VP8', 'VP9'],
                codecPreferenceOrder: ['H264', 'VP8', 'VP9'],
              },
              disableModeratorIndicator: true,
            },
          });
          trackVideoCallStarted(session, isNew);
          await notifyAboutJoinToTheVideoChat(session.user_id, session.psychologist_id);
        } catch (error) {
          logError(error, {
            consultationID: session.id,
          });
        } finally {
          setIsLoading(false);
        }
      } else if (isNew) {
        trackVideoCallStarted(session, isNew);
        setIsLoading(false);
        history.push(
          PrivateRoutes.VIDEO_CHAT_JITSI.replace(':psychologistID', session.psychologist_id).replace(
            ':consultationID',
            session.id
          )
        );
      } else {
        trackVideoCallStarted(session, isNew);
        setIsLoading(false);
        history.push(
          PrivateRoutes.VIDEO_CHAT.replace(':psychologistID', session.psychologist_id).replace(
            ':consultationID',
            session.id
          )
        );
      }
    },
    [history, isVideoCallActive]
  );

  return {
    handleModalVideoClick,
    isLoading,
  };
};
